import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import Base from '../components/Base';
import AuthContext from '../context/AuthContext';
import {
  BarChart3, TrendingUp, ShoppingBag, IndianRupee, Package,
  Eye, Users, Search, ChevronLeft, ChevronRight, Globe, Monitor,
  UserPlus, UserCheck, ArrowDown, Clock, Lightbulb, Download,
  ShoppingCart, AlertTriangle, CheckCircle, Info, XCircle, X,
  Smartphone, Tablet, ArrowUpRight, ArrowDownRight, Star, Repeat
} from 'lucide-react';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
// ── Constants ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'traffic', label: 'Traffic', icon: Globe },
  { id: 'products', label: 'Products', icon: Eye },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
];

const COUNTRY_FLAGS = {
  IN: '\ud83c\uddee\ud83c\uddf3', US: '\ud83c\uddfa\ud83c\uddf8', GB: '\ud83c\uddec\ud83c\udde7', CA: '\ud83c\udde8\ud83c\udde6',
  AU: '\ud83c\udde6\ud83c\uddfa', AE: '\ud83c\udde6\ud83c\uddea', SG: '\ud83c\uddf8\ud83c\uddec', DE: '\ud83c\udde9\ud83c\uddea',
  FR: '\ud83c\uddeb\ud83c\uddf7', JP: '\ud83c\uddef\ud83c\uddf5', SA: '\ud83c\uddf8\ud83c\udde6', MY: '\ud83c\uddf2\ud83c\uddfe',
  BD: '\ud83c\udde7\ud83c\udde9', LK: '\ud83c\uddf1\ud83c\uddf0', NP: '\ud83c\uddf3\ud83c\uddf5', KR: '\ud83c\uddf0\ud83c\uddf7',
  IT: '\ud83c\uddee\ud83c\uddf9', ES: '\ud83c\uddea\ud83c\uddf8', NL: '\ud83c\uddf3\ud83c\uddf1', TH: '\ud83c\uddf9\ud83c\udded',
};

// ── Geography Tab Component (Unified — Traffic + Orders + Percentages in one view) ──
const COUNTRY_COLORS = ['#d45614', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#06B6D4'];

const GeographyTab = ({ data, loading }) => {
  if (loading) return <LoadingState text="Loading geography data..." />;
  if (!data) return <EmptyState text="No geography data available yet. Browse your store to generate traffic data." icon={<Globe className="w-12 h-12" />} />;

  const { visitor_geography = [], order_geography = [], split = {}, total_tracked_views = 0, total_all_views = 0, untracked_views = 0 } = data;
  const totalVisitors = visitor_geography.reduce((sum, v) => sum + v.visitors, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <HeroStatCard label="Total Tracked Visits" value={total_tracked_views} icon={<Eye className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={untracked_views > 0 ? `${total_all_views} total (${untracked_views} before tracking)` : undefined} />
        <HeroStatCard label="Countries" value={visitor_geography.length} icon={<Globe className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
        <HeroStatCard label="Unique Visitors" value={totalVisitors} icon={<Users className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
        <HeroStatCard label="Domestic Orders" value={split.domestic_orders || 0} icon={<Package className="w-5 h-5" />} gradient="from-orange-500 to-red-500" sub={fmt(split.domestic_revenue)} />
        <HeroStatCard label="Intl Orders" value={split.international_orders || 0} icon={<Globe className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" sub={fmt(split.international_revenue)} />
      </div>

      {/* Unified Country Table — Traffic + Orders in one view */}
      <SectionCard title="Traffic & Orders by Country" icon={<Globe className="w-5 h-5 text-[#d45614]" />}>
        <div className="p-6">
          {visitor_geography.length === 0 ? (
            <EmptyState text="No country data yet. New visits will be tracked automatically." icon={<Globe className="w-12 h-12" />} />
          ) : (
            <>
              {/* Visual bars */}
              <div className="space-y-5 mb-8">
                {visitor_geography.map((item, i) => {
                  const flag = COUNTRY_FLAGS[item.country_code] || '\ud83c\udf10';
                  const color = COUNTRY_COLORS[i % COUNTRY_COLORS.length];
                  const orderData = order_geography.find(o => o.country_code === item.country_code);
                  return (
                    <div key={item.country_code} className="group hover:bg-gray-50/50 -mx-3 px-3 py-2 rounded-xl transition-all duration-200">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl w-10 text-center flex-shrink-0">{flag}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                              <span className="text-sm font-bold text-gray-900">{item.country_name}</span>
                              <span className="text-xs px-2.5 py-1 rounded-full font-bold shadow-sm" style={{ backgroundColor: color + '18', color: color, border: `1px solid ${color}25` }}>{item.percentage}%</span>
                            </div>
                            <div className="flex items-center gap-5 text-xs text-gray-500">
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /><b className="text-gray-700">{item.views}</b></span>
                              <span className="flex items-center gap-1"><Users className="w-3 h-3" /><b className="text-gray-700">{item.visitors}</b></span>
                              <span className="hidden sm:flex items-center gap-1"><Monitor className="w-3 h-3" /><b className="text-gray-700">{item.sessions}</b></span>
                              {orderData && <span className="text-[#d45614] font-bold">{orderData.orders} orders &middot; {fmt(orderData.revenue)}</span>}
                            </div>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: item.percentage + '%', background: `linear-gradient(90deg, ${color}, ${color}CC)` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Compact data table below */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-xs text-gray-400 uppercase font-bold tracking-wider mb-4">Detailed Breakdown</p>
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/80">
                        <th className="text-left text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3 pl-4">Country</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Views</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Visitors</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Sessions</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Share</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Orders</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3 pr-4">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitor_geography.map((item, i) => {
                        const flag = COUNTRY_FLAGS[item.country_code] || '\ud83c\udf10';
                        const orderData = order_geography.find(o => o.country_code === item.country_code);
                        return (
                          <tr key={item.country_code} className="border-t border-gray-50 hover:bg-blue-50/30 transition-colors">
                            <td className="py-3 pl-4"><span className="mr-2">{flag}</span><span className="font-semibold text-gray-800">{item.country_name}</span></td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.views}</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.visitors}</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.sessions}</td>
                            <td className="py-3 text-right font-bold tabular-nums" style={{ color: COUNTRY_COLORS[i % COUNTRY_COLORS.length] }}>{item.percentage}%</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{orderData ? orderData.orders : '—'}</td>
                            <td className="py-3 text-right font-semibold text-gray-800 pr-4 tabular-nums">{orderData ? fmt(orderData.revenue) : '—'}</td>
                          </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr className="border-t-2 border-gray-200 bg-gray-50/60 font-bold text-gray-900">
                        <td className="py-3 pl-4">Total</td>
                        <td className="py-3 text-right tabular-nums">{total_tracked_views}</td>
                        <td className="py-3 text-right tabular-nums">{totalVisitors}</td>
                        <td className="py-3 text-right tabular-nums">{visitor_geography.reduce((s, v) => s + v.sessions, 0)}</td>
                        <td className="py-3 text-right tabular-nums">100%</td>
                        <td className="py-3 text-right tabular-nums">{(split.domestic_orders || 0) + (split.international_orders || 0)}</td>
                        <td className="py-3 text-right pr-4 tabular-nums">{fmt((split.domestic_revenue || 0) + (split.international_revenue || 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

const DAY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const PAGE_TYPE_LABELS = {
  home: 'Home', shop: 'Shop', product: 'Product', category: 'Category',
  search: 'Search', cart: 'Cart', checkout: 'Checkout', other: 'Other',
};

const PAGE_TYPE_COLORS = {
  home: '#d45614', shop: '#3B82F6', product: '#10B981', category: '#8B5CF6',
  search: '#F59E0B', cart: '#EC4899', checkout: '#6366F1', other: '#9CA3AF',
};

const FUNNEL_COLORS = ['#d45614', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];
const FUNNEL_LABELS = { home: 'Home', shop: 'Shop', product: 'Product', cart: 'Cart', checkout: 'Checkout' };

const DEVICE_COLORS = { Mobile: '#d45614', Desktop: '#3B82F6', Tablet: '#10B981' };

const ALERT_STYLES = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CheckCircle, iconColor: 'text-emerald-500', glow: 'shadow-emerald-100' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-500', glow: 'shadow-amber-100' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info, iconColor: 'text-blue-500', glow: 'shadow-blue-100' },
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: XCircle, iconColor: 'text-red-500', glow: 'shadow-red-100' },
};

const fmt = (val) => `\u20B9${Number(val || 0).toLocaleString('en-IN')}`;

// ── CSV Export Utility ─────────────────────────────────────────────────────
const downloadCSV = (headers, rows, filename) => {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// ── Reusable Components ─────────────────────────────────────────────────────

// Premium Hero Stat Card with gradient icon background
const HeroStatCard = ({ label, value, icon, gradient, sub, growth }) => (
  <div className="group relative bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
    {/* Subtle gradient glow in top-right */}
    <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${gradient} opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-300`} />
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-md`}>
          {icon}
        </div>
      </div>
      <p className="text-3xl font-extrabold text-gray-900 font-fell tabular-nums">{value}</p>
      {growth !== undefined && growth !== null && (
        <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold ${growth >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
          {growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {growth >= 0 ? '+' : ''}{growth}% vs prev
        </div>
      )}
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  </div>
);

// Keep original StatCard for backward compatibility within sub-components
const StatCard = ({ label, value, icon, color, bg, sub, growth }) => (
  <HeroStatCard
    label={label}
    value={value}
    icon={icon}
    gradient={
      color === '#d45614' ? 'from-orange-500 to-amber-500' :
      color === '#10B981' ? 'from-emerald-500 to-teal-500' :
      color === '#3B82F6' ? 'from-blue-500 to-cyan-500' :
      color === '#8B5CF6' ? 'from-purple-500 to-indigo-500' :
      'from-gray-500 to-gray-600'
    }
    sub={sub}
    growth={growth}
  />
);

const LoadingState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="relative">
      <div className="w-14 h-14 border-4 border-gray-200 rounded-full" />
      <div className="absolute inset-0 w-14 h-14 border-4 border-[#d45614] border-t-transparent rounded-full animate-spin" />
    </div>
    <p className="text-gray-400 mt-5 text-sm font-medium">{text}</p>
  </div>
);

const EmptyState = ({ text, icon }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-300">
    {icon && <div className="mb-4 opacity-40">{icon}</div>}
    {!icon && (
      <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
        <BarChart3 className="w-8 h-8 text-gray-200" />
      </div>
    )}
    <p className="text-sm text-gray-400 max-w-xs text-center leading-relaxed">{text}</p>
  </div>
);

const SectionCard = ({ title, icon, children, action }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6 overflow-hidden hover:shadow-md transition-shadow duration-300">
    <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
      <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2.5">
        {icon}
        {title}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

const ExportButton = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 active:scale-95">
    <Download className="w-3.5 h-3.5" />
    Export CSV
  </button>
);

const AlertCard = ({ alert, onDismiss }) => {
  const style = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
  const Icon = style.icon;
  return (
    <div className={`${style.bg} ${style.border} border rounded-2xl p-4 flex items-start gap-3 shadow-sm ${style.glow} hover:shadow-md transition-all duration-200`}>
      <div className={`w-9 h-9 rounded-xl ${style.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${style.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${style.text}`}>{alert.title}</p>
        <p className={`text-xs ${style.text} opacity-75 mt-1 leading-relaxed`}>{alert.message}</p>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-gray-300 hover:text-gray-500 shrink-0 p-1 rounded-lg hover:bg-white/50 transition-all">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ConversionBadge = ({ rate }) => {
  const config = rate >= 20
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : rate >= 10
    ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return <span className={`${config} text-xs font-bold px-2.5 py-1 rounded-lg border`}>{rate}%</span>;
};

// ── Donut Chart (pure CSS/SVG) ─────────────────────────────────────────────
const DonutChart = ({ segments, total, size = 160 }) => {
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercent = 0;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 160 160" className="-rotate-90">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="16" />
        {segments.map((seg, i) => {
          const percent = total > 0 ? seg.count / total : 0;
          const dashLength = percent * circumference;
          const dashOffset = cumulativePercent * circumference;
          cumulativePercent += percent;
          return (
            <circle
              key={i}
              cx="80" cy="80" r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth="16"
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-dashOffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-extrabold text-gray-900 font-fell">{total}</span>
        <span className="text-xs text-gray-400 font-medium">Total</span>
      </div>
    </div>
  );
};

// ── Mini Sparkline Bar (for stat cards) ────────────────────────────────────
const MiniBarChart = ({ data, color = '#d45614', height = 32 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-px" style={{ height }}>
      {data.slice(-14).map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm opacity-70 hover:opacity-100 transition-opacity"
          style={{ height: `${Math.max((v / max) * 100, 4)}%`, backgroundColor: color, minWidth: 2 }}
        />
      ))}
    </div>
  );
};

// ── Overview Tab ────────────────────────────────────────────────────────────
const OverviewTab = ({ overview, topProducts, statusData, funnel, funnelLoading, comparison, alerts, dismissedAlerts, onDismissAlert }) => {
  const statusTotal = statusData
    ? statusData.pending + statusData.in_progress + statusData.ready + statusData.delivered
    : 0;

  const statusSegments = statusData && statusTotal > 0 ? [
    { label: 'Pending', count: statusData.pending, color: '#F97316' },
    { label: 'In Progress', count: statusData.in_progress, color: '#F59E0B' },
    { label: 'Ready', count: statusData.ready, color: '#3B82F6' },
    { label: 'Delivered', count: statusData.delivered, color: '#10B981' },
  ] : [];

  const funnelMax = funnel ? Math.max(...funnel.map(f => f.sessions), 1) : 1;

  const visibleAlerts = (alerts || []).filter((_, i) => !dismissedAlerts.has(i));
  const g = comparison?.growth;

  // Quick summary
  const curOrders = comparison?.current?.orders || 0;
  const curRevenue = comparison?.current?.revenue || 0;
  const ordersGrowth = g?.orders;

  // Calculate revenue per top product for progress bars
  const maxProductRevenue = topProducts.length > 0
    ? Math.max(...topProducts.map(p => p.revenue || 0), 1) : 1;

  return (
    <>
      {/* Smart Alerts */}
      {visibleAlerts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          {visibleAlerts.map((alert, idx) => {
            const realIndex = (alerts || []).indexOf(alert);
            return <AlertCard key={realIndex} alert={alert} onDismiss={() => onDismissAlert(realIndex)} />;
          })}
        </div>
      )}

      {/* Quick Summary Banner */}
      {comparison && curOrders > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-[#d45614] to-[#e8722e] rounded-2xl p-5 mb-7 shadow-lg shadow-orange-100">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-12 -mt-12" />
          <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-white/5 rounded-full -mb-16" />
          <div className="relative">
            <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">Period Summary</p>
            <p className="text-white text-lg font-bold">
              <span className="text-2xl font-extrabold">{curOrders}</span> orders worth <span className="text-2xl font-extrabold">{fmt(curRevenue)}</span>
              {ordersGrowth !== undefined && ordersGrowth !== 0 && (
                <span className={`ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${ordersGrowth > 0 ? 'bg-white/20 text-white' : 'bg-red-400/30 text-white'}`}>
                  {ordersGrowth > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {ordersGrowth > 0 ? '+' : ''}{Math.abs(ordersGrowth)}%
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Hero Stat Cards with Growth */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <HeroStatCard label="Total Orders" value={overview?.total_orders || 0} icon={<ShoppingBag className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={overview?.avg_order_value > 0 ? `Avg: ${fmt(overview.avg_order_value)}` : null} growth={g?.orders} />
        <HeroStatCard label="Total Revenue" value={fmt(overview?.total_revenue)} icon={<IndianRupee className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" growth={g?.revenue} />
        <HeroStatCard label="This Month Orders" value={overview?.this_month_orders || 0} icon={<TrendingUp className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" growth={g?.views} />
        <HeroStatCard label="This Month Revenue" value={fmt(overview?.this_month_revenue)} icon={<IndianRupee className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" growth={g?.visitors} />
      </div>

      {/* Two-column: Funnel + Status Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* Conversion Funnel — takes 3 cols */}
        <div className="lg:col-span-3">
          <SectionCard title="Conversion Funnel" icon={<ArrowDown className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              {funnelLoading ? (
                <div className="text-center py-8 text-gray-300 text-sm">Loading funnel...</div>
              ) : funnel && funnel.length > 0 ? (
                <div className="space-y-4">
                  {funnel.map((step, i) => {
                    const prevSessions = i > 0 ? funnel[i - 1].sessions : step.sessions;
                    const dropRate = prevSessions > 0 && i > 0
                      ? Math.round(((prevSessions - step.sessions) / prevSessions) * 100)
                      : null;
                    const widthPercent = Math.max((step.sessions / funnelMax) * 100, 12);
                    return (
                      <div key={step.stage} className="group">
                        <div className="flex items-center gap-4">
                          <span className="w-20 text-sm text-gray-500 text-right shrink-0 font-semibold">
                            {FUNNEL_LABELS[step.stage] || step.stage}
                          </span>
                          <div className="flex-1 relative">
                            <div className="bg-gray-50 rounded-xl h-10 overflow-hidden">
                              <div
                                className="h-full rounded-xl flex items-center px-4 text-white text-xs font-bold transition-all duration-700 ease-out relative overflow-hidden"
                                style={{
                                  width: `${widthPercent}%`,
                                  background: `linear-gradient(135deg, ${FUNNEL_COLORS[i] || '#9CA3AF'}, ${FUNNEL_COLORS[i] || '#9CA3AF'}BB)`,
                                }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0" />
                                <span className="relative">{step.sessions} sessions</span>
                              </div>
                            </div>
                          </div>
                          {dropRate !== null && dropRate > 0 && (
                            <div className="flex items-center gap-1 w-16 justify-end shrink-0">
                              <ArrowDownRight className="w-3 h-3 text-red-400" />
                              <span className="text-xs font-bold text-red-400">-{dropRate}%</span>
                            </div>
                          )}
                          {(dropRate === null || dropRate === 0) && (
                            <div className="w-16 shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="No funnel data yet" icon={<ArrowDown className="w-12 h-12" />} />
              )}
            </div>
          </SectionCard>
        </div>

        {/* Orders by Status Donut — takes 2 cols */}
        <div className="lg:col-span-2">
          <SectionCard title="Order Status" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              {statusTotal > 0 ? (
                <div className="flex flex-col items-center">
                  <DonutChart segments={statusSegments} total={statusTotal} size={180} />
                  <div className="grid grid-cols-2 gap-3 mt-6 w-full">
                    {statusSegments.map((seg, i) => (
                      <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 bg-gray-50 rounded-xl">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                        <div className="min-w-0">
                          <span className="text-xs text-gray-500 block">{seg.label}</span>
                          <span className="text-sm font-bold text-gray-900">{seg.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState text="No order data yet" icon={<ShoppingBag className="w-12 h-12" />} />
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Top Products by Orders — Premium Cards */}
      <SectionCard title="Top Products" icon={<Package className="w-4 h-4 text-[#d45614]" />}>
        {topProducts.length > 0 ? (
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topProducts.slice(0, 6).map((p, i) => (
                <div key={i} className="group relative bg-gray-50/50 hover:bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all duration-300">
                  {/* Rank badge */}
                  <div className={`absolute -top-2 -left-2 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md ${i === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500' : i === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' : i === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400' : 'bg-gray-300'}`}>
                    {i + 1}
                  </div>
                  <div className="flex items-start gap-3.5 mt-1">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-14 h-14 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate mb-1">{p.name}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-[#d45614]">{p.orders} orders</span>
                        <span className="text-xs text-gray-400">{fmt(p.revenue)}</span>
                      </div>
                      {/* Revenue progress bar */}
                      <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(p.revenue / maxProductRevenue) * 100}%`,
                            background: 'linear-gradient(90deg, #d45614, #e8722e)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState text="No order data yet" icon={<Package className="w-12 h-12" />} />
        )}
      </SectionCard>
    </>
  );
};

// ── Traffic Tab ─────────────────────────────────────────────────────────────
const TrafficTab = ({ traffic, referrers, peakHours, devices, loading, geoData, geoLoading }) => {
  if (loading) return <LoadingState text="Loading traffic data..." />;
  if (!traffic) return <EmptyState text="No traffic data yet. Page views will appear here once customers visit your store." icon={<Globe className="w-12 h-12" />} />;

  const maxDaily = Math.max(...(traffic.daily_views || []).map(d => d.views), 1);

  const handleExportTraffic = () => {
    const headers = ['Date', 'Views'];
    const rows = (traffic.daily_views || []).map(d => [d.date, d.views]);
    downloadCSV(headers, rows, 'traffic_data');
  };

  return (
    <>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <HeroStatCard label="Total Page Views" value={traffic.total_views} icon={<Eye className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={`Last ${traffic.days} days`} />
        <HeroStatCard label="Unique Visitors" value={traffic.unique_visitors} icon={<Users className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
        <HeroStatCard label="New Visitors" value={traffic.new_visitors ?? 0} icon={<UserPlus className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
        <HeroStatCard label="Returning Visitors" value={traffic.returning_visitors ?? 0} icon={<UserCheck className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" />
      </div>

      {/* Country Traffic Breakdown — full bars + table */}
      {traffic.top_countries && traffic.top_countries.length > 0 && (
        <SectionCard title="Traffic by Country" icon={<Globe className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-6 space-y-5">
            {traffic.top_countries.map((c, i) => {
              const flag = COUNTRY_FLAGS[c.country_code] || '\ud83c\udf10';
              const color = COUNTRY_COLORS[i % COUNTRY_COLORS.length];
              const geoOrders = geoData?.order_geography?.find(o => o.country_code === c.country_code);
              return (
                <div key={c.country_code} className="group hover:bg-gray-50/50 -mx-3 px-3 py-2 rounded-xl transition-all duration-200">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl w-10 text-center flex-shrink-0">{flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-sm font-bold text-gray-900">{c.country_name || c.country_code}</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-bold shadow-sm" style={{ backgroundColor: color + '18', color: color, border: `1px solid ${color}25` }}>{c.percentage}%</span>
                        </div>
                        <div className="flex items-center gap-5 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /><b className="text-gray-700">{c.count}</b></span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /><b className="text-gray-700">{c.visitors}</b></span>
                          {geoOrders && <span className="text-[#d45614] font-bold">{geoOrders.orders} orders &middot; {fmt(geoOrders.revenue)}</span>}
                        </div>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: c.percentage + '%', background: `linear-gradient(90deg, ${color}, ${color}CC)` }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Domestic vs International summary */}
            {geoData?.split && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mt-3">
                <div className="relative overflow-hidden p-5 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-orange-200/20 rounded-full" />
                  <Package className="w-5 h-5 text-[#d45614] mb-2" />
                  <p className="text-3xl font-extrabold text-[#d45614]">{geoData.split.domestic_orders || 0}</p>
                  <p className="text-xs text-gray-500 mt-1 font-medium">Domestic Orders</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{fmt(geoData.split.domestic_revenue)}</p>
                </div>
                <div className="relative overflow-hidden p-5 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-200/20 rounded-full" />
                  <Globe className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="text-3xl font-extrabold text-blue-600">{geoData.split.international_orders || 0}</p>
                  <p className="text-xs text-gray-500 mt-1 font-medium">International Orders</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{fmt(geoData.split.international_revenue)}</p>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Daily Views — Premium Bar Chart */}
      {traffic.daily_views && traffic.daily_views.length > 0 && (
        <SectionCard title="Daily Views" icon={<TrendingUp className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportTraffic} />}>
          <div className="p-6">
            <div className="flex items-end gap-[3px] h-48">
              {traffic.daily_views.map((d, i) => {
                const heightPct = Math.max((d.views / maxDaily) * 100, 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-10 shadow-lg pointer-events-none">
                      <div className="text-[10px] text-gray-400">{d.date}</div>
                      <div className="font-bold">{d.views} views</div>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                    </div>
                    <div
                      className="w-full rounded-t-md transition-all duration-300 group-hover:opacity-80 cursor-pointer"
                      style={{
                        height: `${heightPct}%`,
                        background: `linear-gradient(180deg, #d45614, #d4561488)`,
                        minHeight: '3px',
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-3 text-xs text-gray-400 font-medium">
              <span>{traffic.daily_views[0]?.date}</span>
              {traffic.daily_views.length > 2 && (
                <span>{traffic.daily_views[Math.floor(traffic.daily_views.length / 2)]?.date}</span>
              )}
              <span>{traffic.daily_views[traffic.daily_views.length - 1]?.date}</span>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Views by Page Type + Referrers side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {traffic.by_page_type && traffic.by_page_type.length > 0 && (
          <SectionCard title="Views by Page Type" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6 space-y-4">
              {traffic.by_page_type.map((item, i) => {
                const maxCount = traffic.by_page_type[0]?.count || 1;
                const color = PAGE_TYPE_COLORS[item.page_type] || '#9CA3AF';
                const pct = Math.round((item.count / (traffic.total_views || 1)) * 100);
                return (
                  <div key={i} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-sm font-semibold text-gray-700">
                          {PAGE_TYPE_LABELS[item.page_type] || item.page_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-400">{pct}%</span>
                        <span className="text-sm font-bold text-gray-900 tabular-nums">{item.count}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max((item.count / maxCount) * 100, 4)}%`, background: `linear-gradient(90deg, ${color}, ${color}BB)` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {referrers && referrers.length > 0 && (
          <SectionCard title="Traffic Sources" icon={<Globe className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-2">
              {referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-white flex items-center justify-center border border-gray-100 transition-colors">
                      <Globe className="w-4 h-4 text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{r.source}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg">{r.visits}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Device Breakdown — Premium Cards */}
      {devices && devices.length > 0 && (
        <SectionCard title="Device Breakdown" icon={<Monitor className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-6">
            {/* Segmented bar */}
            <div className="flex items-center h-4 rounded-full overflow-hidden mb-6 shadow-inner bg-gray-100">
              {devices.map((d) => (
                <div
                  key={d.device}
                  className="h-full transition-all duration-500 first:rounded-l-full last:rounded-r-full"
                  style={{ width: `${d.percent}%`, backgroundColor: DEVICE_COLORS[d.device] || '#9CA3AF', minWidth: d.percent > 0 ? '8px' : '0' }}
                />
              ))}
            </div>
            {/* Device cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {devices.map((d) => {
                const DeviceIcon = d.device === 'Mobile' ? Smartphone : d.device === 'Tablet' ? Tablet : Monitor;
                const deviceColor = DEVICE_COLORS[d.device] || '#9CA3AF';
                return (
                  <div key={d.device} className="group relative overflow-hidden flex items-center gap-4 p-4 bg-gray-50/80 hover:bg-white border border-transparent hover:border-gray-200 rounded-2xl hover:shadow-lg transition-all duration-300">
                    <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-[0.06]`} style={{ backgroundColor: deviceColor }} />
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: deviceColor + '15' }}>
                      <DeviceIcon className="w-6 h-6" style={{ color: deviceColor }} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-900">{d.device}</p>
                      <p className="text-xs text-gray-500">{d.views} views</p>
                    </div>
                    <div className="ml-auto">
                      <p className="text-2xl font-extrabold tabular-nums" style={{ color: deviceColor }}>{d.percent}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Peak Hours + Day of Week side by side */}
      {peakHours && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Views by Hour" icon={<Clock className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              <div className="flex items-end gap-[2px] h-36">
                {peakHours.hours.map((h) => {
                  const maxH = Math.max(...peakHours.hours.map(x => x.views), 1);
                  const isPeak = h.views === Math.max(...peakHours.hours.map(x => x.views));
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-10 shadow-lg pointer-events-none">
                        {h.hour}:00 — {h.views} views
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                      </div>
                      <div
                        className="w-full rounded-t transition-all duration-300 cursor-pointer"
                        style={{
                          height: `${Math.max((h.views / maxH) * 100, 3)}%`,
                          background: isPeak ? 'linear-gradient(180deg, #d45614, #d4561488)' : '#e5e7eb',
                          minHeight: '3px',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-400 font-medium">
                <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Views by Day" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6 space-y-3">
              {peakHours.weekdays.map((d) => {
                const maxD = Math.max(...peakHours.weekdays.map(x => x.views), 1);
                const isPeak = d.views === maxD;
                return (
                  <div key={d.day} className="group flex items-center gap-3">
                    <span className="w-10 text-xs text-gray-500 text-right shrink-0 font-semibold">{d.day}</span>
                    <div className="flex-1 bg-gray-50 rounded-lg h-8 overflow-hidden">
                      <div
                        className="h-full rounded-lg flex items-center justify-end pr-3 text-xs font-bold transition-all duration-500"
                        style={{
                          width: `${Math.max((d.views / maxD) * 100, 8)}%`,
                          background: isPeak
                            ? 'linear-gradient(90deg, #d45614, #e8722e)'
                            : 'linear-gradient(90deg, #e5e7eb, #d1d5db)',
                          color: isPeak ? 'white' : '#6b7280',
                        }}
                      >
                        {d.views}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>
      )}
    </>
  );
};

// ── Products Tab ────────────────────────────────────────────────────────────
const ProductsTab = ({ topPages, categories, conversion, abandonedCarts, loading }) => {
  if (loading) return <LoadingState text="Loading product data..." />;

  const handleExportProducts = () => {
    if (!conversion || !conversion.length) return;
    const headers = ['Product', 'Views', 'Cart Adds', 'Orders', 'View\u2192Cart %', 'Cart\u2192Order %'];
    const rows = conversion.map(p => [p.name, p.views, p.cart_adds, p.orders, p.view_to_cart, p.cart_to_order]);
    downloadCSV(headers, rows, 'product_conversion');
  };

  return (
    <>
      {/* Product Conversion Funnel */}
      <SectionCard title="Product Conversion Funnel" icon={<ShoppingCart className="w-4 h-4 text-[#d45614]" />} action={conversion && conversion.length > 0 && <ExportButton onClick={handleExportProducts} />}>
        {conversion && conversion.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-4">Product</div>
              <div className="col-span-1 text-right">Views</div>
              <div className="col-span-2 text-right">Cart Adds</div>
              <div className="col-span-1 text-right">Orders</div>
              <div className="col-span-2 text-right">View\u2192Cart</div>
              <div className="col-span-2 text-right">Cart\u2192Order</div>
            </div>
            <div className="divide-y divide-gray-50">
              {conversion.map((p, idx) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                  <div className="col-span-4 flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-300 w-5">{idx + 1}</span>
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-10 h-10 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm group-hover:shadow-md transition-shadow" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                  </div>
                  <div className="col-span-1 text-right"><p className="text-sm text-gray-600 tabular-nums">{p.views}</p></div>
                  <div className="col-span-2 text-right"><p className="text-sm text-gray-600 tabular-nums">{p.cart_adds}</p></div>
                  <div className="col-span-1 text-right"><p className="text-sm font-bold text-gray-900 tabular-nums">{p.orders}</p></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.view_to_cart} /></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.cart_to_order} /></div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product conversion data yet. Views and cart additions will appear here." icon={<ShoppingCart className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Abandoned Carts — Premium Warning */}
      <SectionCard title="Abandoned Carts" icon={<ShoppingCart className="w-4 h-4 text-amber-500" />}>
        {abandonedCarts && abandonedCarts.total_abandoned_carts > 0 ? (
          <>
            <div className="px-6 py-4 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-900">
                    {abandonedCarts.total_abandoned_carts} abandoned carts worth {fmt(abandonedCarts.total_abandoned_value)}
                  </p>
                  <p className="text-xs text-amber-700/70">Recovery opportunity — consider sending follow-up emails</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {(abandonedCarts.carts || []).slice(0, 10).map((c) => (
                <div key={c.customer_id} className="px-6 py-4 hover:bg-amber-50/30 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                      <div>
                        <span className="text-sm font-bold text-gray-900">{c.customer_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{c.email}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{fmt(c.total_value)}</p>
                      <p className="text-xs text-gray-400">{c.item_count} item{c.item_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No abandoned carts found." icon={<ShoppingCart className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Most Viewed Products */}
      <SectionCard title="Most Viewed Products" icon={<Eye className="w-4 h-4 text-[#d45614]" />}>
        {topPages && topPages.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-1">#</div>
              <div className="col-span-6">Product</div>
              <div className="col-span-3 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
            </div>
            <div className="divide-y divide-gray-50">
              {topPages.map((p, i) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                  <div className="col-span-1">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-gradient-to-br from-orange-100 to-amber-100 text-[#d45614]' : 'bg-gray-50 text-gray-400'}`}>{i + 1}</span>
                  </div>
                  <div className="col-span-6 flex items-center gap-3">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-10 h-10 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className="text-sm font-bold text-gray-900 tabular-nums">{p.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500 tabular-nums">{p.unique_visitors}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product view data yet." icon={<Eye className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Category Performance */}
      <SectionCard title="Category Performance" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
        {categories && categories.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-4">Category</div>
              <div className="col-span-2 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
              <div className="col-span-2 text-right">Orders</div>
              <div className="col-span-2 text-right">Revenue</div>
            </div>
            <div className="divide-y divide-gray-50">
              {categories.map((c, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200">
                  <div className="col-span-4 flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COUNTRY_COLORS[i % COUNTRY_COLORS.length] }} />
                    <p className="text-sm font-bold text-gray-900 truncate">{c.category}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-600 tabular-nums">{c.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500 tabular-nums">{c.visitors}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-bold text-gray-900 tabular-nums">{c.orders}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-semibold text-[#d45614] tabular-nums">{fmt(c.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No category data yet." icon={<BarChart3 className="w-12 h-12" />} />
        )}
      </SectionCard>
    </>
  );
};

// ── Customers Tab ───────────────────────────────────────────────────────────
const CustomersTab = ({ authAxios, customerValue }) => {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCustomers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    try {
      const res = await authAxios.get('/analytics/customers/', { params: { page: p, search: q, page_size: 15 } });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setPage(res.data.page || 1);
    } catch (err) {
      devLog.error('Customer fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchCustomers(1, ''); }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1, search);
  };

  const summary = customerValue?.summary;
  const topCustomers = customerValue?.top_customers || [];

  const handleExportCustomers = () => {
    if (!topCustomers.length) return;
    const headers = ['Name', 'Email', 'Total Orders', 'Total Spend', 'Avg Order Value', 'First Order', 'Last Order'];
    const rows = topCustomers.map(c => [c.name, c.email, c.total_orders, c.total_spend, c.avg_order_value, c.first_order, c.last_order]);
    downloadCSV(headers, rows, 'customer_value');
  };

  return (
    <>
      {/* LTV Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
          <HeroStatCard label="Total Customers" value={summary.total_customers} icon={<Users className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" />
          <HeroStatCard label="Repeat Buyers" value={summary.repeat_buyers} icon={<Repeat className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
          <HeroStatCard label="Repeat Rate" value={`${summary.repeat_rate}%`} icon={<TrendingUp className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
          <HeroStatCard label="Avg Lifetime Value" value={fmt(summary.avg_ltv)} icon={<Star className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" />
        </div>
      )}

      {/* Top Customers by Spend */}
      {topCustomers.length > 0 && (
        <SectionCard title="Top Customers by Spend" icon={<Star className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportCustomers} />}>
          <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1 text-right">Orders</div>
            <div className="col-span-2 text-right">Total Spend</div>
            <div className="col-span-1 text-right">Avg</div>
            <div className="col-span-2 text-right">Last Order</div>
          </div>
          <div className="divide-y divide-gray-50">
            {topCustomers.map((c, i) => (
              <div key={c.customer_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                <div className="col-span-3 flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-gradient-to-br from-amber-100 to-orange-100 text-[#d45614]' : 'bg-gray-50 text-gray-400'}`}>{i + 1}</div>
                  <p className="text-sm font-bold text-gray-900 truncate">{c.name}</p>
                </div>
                <div className="col-span-3"><p className="text-sm text-gray-500 truncate">{c.email}</p></div>
                <div className="col-span-1 text-right"><p className="text-sm font-semibold text-gray-700 tabular-nums">{c.total_orders}</p></div>
                <div className="col-span-2 text-right"><p className="text-sm font-bold text-[#d45614] tabular-nums">{fmt(c.total_spend)}</p></div>
                <div className="col-span-1 text-right"><p className="text-xs text-gray-400 tabular-nums">{fmt(c.avg_order_value)}</p></div>
                <div className="col-span-2 text-right">
                  <p className="text-sm text-gray-500">
                    {c.last_order ? new Date(c.last_order).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '\u2014'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Customer List */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-sm font-bold text-gray-900">{total} registered customer{total !== 1 ? 's' : ''}</p>
          <p className="text-xs text-gray-400">Browse and search your customer base</p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
            <input type="text" placeholder="Search name, email, phone..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-72 pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#d45614]/20 focus:border-[#d45614] transition-all placeholder-gray-300" />
          </div>
          <button type="submit" className="px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white rounded-xl hover:shadow-lg hover:shadow-orange-200 transition-all duration-200 active:scale-95">Search</button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        {loading ? <LoadingState text="Loading customers..." /> : customers.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-3">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-1 text-center">Status</div>
            </div>
            <div className="divide-y divide-gray-50">
              {customers.map((c) => (
                <div key={c.id} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200">
                  <div className="sm:col-span-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center text-xs font-bold text-gray-400 border border-gray-100">
                      {(c.username || '?')[0].toUpperCase()}
                    </div>
                    <p className="text-sm font-bold text-gray-900">{c.username}</p>
                  </div>
                  <div className="sm:col-span-4"><p className="text-sm text-gray-500 truncate">{c.email}</p></div>
                  <div className="sm:col-span-2"><p className="text-sm text-gray-500 tabular-nums">{c.phone || '\u2014'}</p></div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-400">
                      {c.date_joined ? new Date(c.date_joined).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014'}
                    </p>
                  </div>
                  <div className="sm:col-span-1 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${c.is_verified ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.is_verified ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      {c.is_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 font-medium">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => fetchCustomers(page - 1, search)} disabled={page <= 1}
                    className="p-2 rounded-xl border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => fetchCustomers(page + 1, search)} disabled={page >= totalPages}
                    className="p-2 rounded-xl border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState text={search ? `No customers matching "${search}"` : 'No registered customers yet.'} icon={<Users className="w-12 h-12" />} />
        )}
      </div>
    </>
  );
};

// ── Insights Tab ────────────────────────────────────────────────────────────
const InsightsTab = ({ alerts, comparison, conversion, abandonedCarts, customerValue, days }) => {
  const g = comparison?.growth;
  const cur = comparison?.current;
  const prev = comparison?.previous;

  // Best selling product from conversion data
  const bestProduct = conversion && conversion.length > 0
    ? conversion.reduce((a, b) => (a.orders > b.orders ? a : b))
    : null;

  // Busiest day from current period
  const busiestMetric = cur?.views > 0 ? `${cur.views} views in last ${days} days` : null;

  return (
    <>
      {/* Growth Metrics — Large visual cards */}
      {comparison && (
        <div className="mb-7">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Orders', cur: cur?.orders, prev: prev?.orders, growth: g?.orders, icon: <ShoppingBag className="w-5 h-5" />, gradient: 'from-orange-500 to-amber-500' },
              { label: 'Revenue', cur: fmt(cur?.revenue), prev: fmt(prev?.revenue), growth: g?.revenue, icon: <IndianRupee className="w-5 h-5" />, gradient: 'from-emerald-500 to-teal-500' },
              { label: 'Page Views', cur: cur?.views, prev: prev?.views, growth: g?.views, icon: <Eye className="w-5 h-5" />, gradient: 'from-blue-500 to-cyan-500' },
              { label: 'Visitors', cur: cur?.visitors, prev: prev?.visitors, growth: g?.visitors, icon: <Users className="w-5 h-5" />, gradient: 'from-purple-500 to-indigo-500' },
            ].map((m) => (
              <div key={m.label} className="group relative bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
                <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${m.gradient} opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-300`} />
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{m.label}</p>
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${m.gradient} flex items-center justify-center text-white shadow-md`}>
                      {m.icon}
                    </div>
                  </div>
                  <p className="text-2xl font-extrabold text-gray-900 font-fell tabular-nums">{m.cur}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-300">vs</span>
                    <span className="text-sm font-semibold text-gray-300 tabular-nums">{m.prev}</span>
                  </div>
                  {m.growth !== undefined && (
                    <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold ${m.growth >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
                      {m.growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {m.growth >= 0 ? '+' : ''}{m.growth}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Smart Alerts */}
      <SectionCard title="Smart Alerts" icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}>
        {alerts && alerts.length > 0 ? (
          <div className="p-5 space-y-3">
            {alerts.map((alert, i) => <AlertCard key={i} alert={alert} />)}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700">All Clear</p>
            <p className="text-xs text-gray-400 mt-1">No alerts right now — everything looks good!</p>
          </div>
        )}
      </SectionCard>

      {/* Key Metrics Summary — Beautiful cards */}
      <SectionCard title="Key Metrics" icon={<Star className="w-4 h-4 text-[#d45614]" />}>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {bestProduct && (
              <div className="group relative overflow-hidden p-5 bg-gradient-to-br from-amber-50/80 to-white border border-amber-100/80 rounded-2xl hover:shadow-lg hover:border-amber-200 transition-all duration-300">
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-amber-200/15 rounded-full" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-md">
                    <Package className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Best Selling Product</p>
                    <p className="text-base font-bold text-gray-900 truncate mt-0.5">{bestProduct.name}</p>
                    <p className="text-sm text-[#d45614] font-bold mt-1">{bestProduct.orders} orders</p>
                  </div>
                </div>
              </div>
            )}
            {busiestMetric && (
              <div className="group relative overflow-hidden p-5 bg-gradient-to-br from-blue-50/80 to-white border border-blue-100/80 rounded-2xl hover:shadow-lg hover:border-blue-200 transition-all duration-300">
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-blue-200/15 rounded-full" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white shadow-md">
                    <Eye className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Traffic</p>
                    <p className="text-base font-bold text-gray-900 mt-0.5">{busiestMetric}</p>
                  </div>
                </div>
              </div>
            )}
            {customerValue?.summary?.repeat_rate > 0 && (
              <div className="group relative overflow-hidden p-5 bg-gradient-to-br from-emerald-50/80 to-white border border-emerald-100/80 rounded-2xl hover:shadow-lg hover:border-emerald-200 transition-all duration-300">
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-emerald-200/15 rounded-full" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white shadow-md">
                    <Repeat className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Customer Retention</p>
                    <p className="text-base font-bold text-gray-900 mt-0.5">{customerValue.summary.repeat_rate}% repeat rate</p>
                    <p className="text-sm text-emerald-600 font-semibold mt-1">{customerValue.summary.repeat_buyers} repeat buyers</p>
                  </div>
                </div>
              </div>
            )}
            {abandonedCarts?.total_abandoned_carts > 0 && (
              <div className="group relative overflow-hidden p-5 bg-gradient-to-br from-red-50/80 to-white border border-red-100/80 rounded-2xl hover:shadow-lg hover:border-red-200 transition-all duration-300">
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-red-200/15 rounded-full" />
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center text-white shadow-md">
                    <ShoppingCart className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Abandoned Carts</p>
                    <p className="text-base font-bold text-gray-900 mt-0.5">{abandonedCarts.total_abandoned_carts} carts</p>
                    <p className="text-sm text-red-600 font-bold mt-1">{fmt(abandonedCarts.total_abandoned_value)} potential revenue</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Quick Action Suggestions */}
      {alerts && alerts.length > 0 && (
        <SectionCard title="Suggested Actions" icon={<Lightbulb className="w-4 h-4 text-amber-500" />}>
          <div className="p-5 space-y-3">
            {alerts.map((alert, i) => {
              let action = '';
              if (alert.icon === 'alert' && alert.title.includes('Stock')) action = 'Consider restocking this product to capture demand';
              else if (alert.icon === 'cart') action = 'Send follow-up emails to customers with abandoned carts';
              else if (alert.title.includes('No Recent')) action = 'Consider running a promotion or sharing on social media';
              else if (alert.icon === 'trending_up') action = 'Great momentum! Keep up your marketing efforts';
              else if (alert.icon === 'trending_down') action = 'Review your marketing channels and try new promotions';
              else if (alert.icon === 'globe') action = `Double down on ${alert.message.split('from ')[1] || 'this channel'}`;
              if (!action) return null;
              return (
                <div key={i} className="flex items-center gap-3.5 p-4 bg-gradient-to-r from-amber-50/50 to-white border border-amber-100/60 rounded-2xl hover:border-amber-200 hover:shadow-sm transition-all duration-200">
                  <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-sm text-gray-700 font-medium">{action}</p>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </>
  );
};

// ── Main Analytics Component ────────────────────────────────────────────────
const Analytics = () => {
  const { authToken } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(30);

  // Overview
  const [overview, setOverview] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Funnel
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  // Comparison & Alerts
  const [comparison, setComparison] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Traffic
  const [traffic, setTraffic] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [devices, setDevices] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);

  // Products
  const [topPages, setTopPages] = useState(null);
  const [categories, setCategories] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);

  // Customers
  const [customerValue, setCustomerValue] = useState(null);

  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: { Authorization: `Bearer ${authToken?.access}` },
  }), [authToken?.access]);

  // Fetch overview on mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const [overviewRes, topRes, statusRes] = await Promise.all([
          authAxios.get('/analytics/overview/'),
          authAxios.get('/analytics/top_products/'),
          authAxios.get('/analytics/orders_by_status/'),
        ]);
        setOverview(overviewRes.data);
        setTopProducts(topRes.data.products || []);
        setStatusData(statusRes.data);
      } catch (err) {
        devLog.error('Analytics fetch error:', err);
      } finally {
        setOverviewLoading(false);
      }
    };
    fetchOverview();
  }, [authAxios]);

  // Fetch comparison + alerts when overview or insights tab active, or days change
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'insights') {
      Promise.all([
        authAxios.get('/analytics/comparison/', { params: { days } }),
        authAxios.get('/analytics/alerts/'),
      ]).then(([compRes, alertRes]) => {
        setComparison(compRes.data);
        setAlerts(alertRes.data.alerts || []);
      }).catch(err => devLog.error('Comparison/alerts fetch error:', err));
    }
  }, [activeTab, days, authAxios]);

  // Fetch funnel when overview tab active or days change
  useEffect(() => {
    if (activeTab === 'overview') {
      setFunnelLoading(true);
      authAxios.get('/analytics/funnel/', { params: { days } })
        .then(res => setFunnel(res.data.funnel || []))
        .catch(err => devLog.error('Funnel fetch error:', err))
        .finally(() => setFunnelLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch traffic data when traffic tab active or days change
  useEffect(() => {
    if (activeTab === 'traffic') {
      setTrafficLoading(true);
      Promise.all([
        authAxios.get('/analytics/traffic/', { params: { days } }),
        authAxios.get('/analytics/referrers/', { params: { days } }),
        authAxios.get('/analytics/peak_hours/', { params: { days } }),
        authAxios.get('/analytics/devices/', { params: { days } }),
      ]).then(([trafficRes, refRes, peakRes, devRes]) => {
        setTraffic(trafficRes.data);
        setReferrers(refRes.data.referrers || []);
        setPeakHours(peakRes.data);
        setDevices(devRes.data.devices || []);
      }).catch(err => devLog.error('Traffic fetch error:', err))
        .finally(() => setTrafficLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch geography data when geography tab active
  useEffect(() => {
    if (activeTab === 'traffic' || activeTab === 'geography') {
      setGeoLoading(true);
      authAxios.get('/analytics/geography/', { params: { days } })
        .then(res => setGeoData(res.data))
        .catch(err => devLog.error('Geography fetch error:', err))
        .finally(() => setGeoLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch products data when products or insights tab active or days change
  useEffect(() => {
    if (activeTab === 'products' || activeTab === 'insights') {
      setProductsLoading(true);
      Promise.all([
        authAxios.get('/analytics/top_pages/', { params: { days } }),
        authAxios.get('/analytics/categories/', { params: { days } }),
        authAxios.get('/analytics/product_conversion/', { params: { days } }),
        authAxios.get('/analytics/abandoned_carts/'),
      ]).then(([topRes, catRes, convRes, cartRes]) => {
        setTopPages(topRes.data.products || []);
        setCategories(catRes.data.categories || []);
        setConversion(convRes.data.products || []);
        setAbandonedCarts(cartRes.data);
      }).catch(err => devLog.error('Products fetch error:', err))
        .finally(() => setProductsLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch customer value when customers or insights tab active
  useEffect(() => {
    if (activeTab === 'customers' || activeTab === 'insights') {
      authAxios.get('/analytics/customer_value/')
        .then(res => setCustomerValue(res.data))
        .catch(err => devLog.error('Customer value fetch error:', err));
    }
  }, [activeTab, authAxios]);

  const handleDismissAlert = useCallback((index) => {
    setDismissedAlerts(prev => new Set([...prev, index]));
  }, []);

  return (
    <Base
      Content={
        <div className="min-h-screen bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
            {/* Header + Day Selector */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d45614] to-[#e8722e] flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-200">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 font-fell tracking-tight">Analytics</h1>
                  <p className="text-sm text-gray-400 mt-0.5">Track performance, discover insights</p>
                </div>
              </div>
              <div className="flex gap-1 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm">
                {DAY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setDays(opt.value)}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-200 ${
                      days === opt.value
                        ? 'bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white shadow-md shadow-orange-200'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabs — Premium pill navigation */}
            <div className="flex gap-1.5 mb-8 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm overflow-x-auto scrollbar-hide">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-lg transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white shadow-md shadow-orange-200'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content with smooth appearance */}
            <div className="animate-fadeIn">
              {activeTab === 'overview' && (
                overviewLoading ? <LoadingState text="Loading analytics..." />
                  : <OverviewTab overview={overview} topProducts={topProducts} statusData={statusData} funnel={funnel} funnelLoading={funnelLoading} comparison={comparison} alerts={alerts} dismissedAlerts={dismissedAlerts} onDismissAlert={handleDismissAlert} />
              )}
              {activeTab === 'traffic' && <TrafficTab traffic={traffic} referrers={referrers} peakHours={peakHours} devices={devices} loading={trafficLoading} geoData={geoData} geoLoading={geoLoading} />}
              {activeTab === 'products' && <ProductsTab topPages={topPages} categories={categories} conversion={conversion} abandonedCarts={abandonedCarts} loading={productsLoading} />}
              {activeTab === 'customers' && <CustomersTab authAxios={authAxios} customerValue={customerValue} />}
              {activeTab === 'insights' && <InsightsTab alerts={alerts} comparison={comparison} conversion={conversion} abandonedCarts={abandonedCarts} customerValue={customerValue} days={days} />}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default Analytics;
